import { useNavigate } from "react-router";

import { Button } from "../../../common/Atoms/Button";
import { Modal } from "../../../common/Atoms/Modal";
import { Notification } from "../../../common/Atoms/Notification";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { ContactTableFields, useContactTableModels } from "../../hooks/useContacts";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useMobileNumberCheck } from "../../hooks/useMobileNumberCheck";
import { useModal } from "../../hooks/useModals";
import { useAiMobileFixEnabled } from "../CRM/CheckMobileNumbersButton";
import { useStartMobileNumberCheckMutation } from "../../services/api/mobileNumberCheckApi/mobileNumberCheck";

/** Progress and result of the "check all mobile numbers" task. Mounted once in Root, opened via the `mobileNumberCheck` modal state */
export function MobileNumberCheckModal() {
  const { state: open, hide } = useModal(`mobileNumberCheck`);
  const { show: showFix } = useModal(`mobileNumberFix`);
  const aiFixEnabled = useAiMobileFixEnabled();
  const currentOrg = useGetSelectedOrganisation();
  const { runningCheck, failedCheck, lastRun } = useMobileNumberCheck();
  const [startCheck, { isLoading: starting }] = useStartMobileNumberCheckMutation();
  const { setFilterModel } = useContactTableModels();
  const navigate = useNavigate();

  function onViewInvalid() {
    setFilterModel({
      items: [
        {
          field: ContactTableFields.mobileNumber,
          operator: `isInvalid`,
          id: Math.round(Math.random() * 1e5),
        },
      ],
    });

    hide();

    // persist: keep the filter above instead of resetting the table models on mount
    navigate(`/${currentOrg.id}/contacts/manage?persist=true`);
  }

  const runButton = (label: string) => (
    <Button
      onClick={ () => startCheck(currentOrg.id) }
      loading={ starting }
      disabled={ starting }
    >
      { label }
    </Button>
  );

  function renderBody() {
    if (runningCheck) {
      return (
        <>
          <Paragraph>{ runningCheck.progressMessage }</Paragraph>
          <div
            className={ `mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200` }
            role={ `progressbar` }
            aria-valuemin={ 0 }
            aria-valuemax={ 100 }
            aria-valuenow={ Math.round(runningCheck.progress * 100) }
          >
            <div
              className={ `h-full rounded-full bg-paidniceGreen-600 transition-all duration-500` }
              style={ { width: `${runningCheck.progress * 100}%` } }
            />
          </div>
          <Paragraph
            variant={ `secondary` }
            className={ `mt-3` }
          >
            { `You can close this window, the check carries on in the background.` }
          </Paragraph>
        </>
      );
    }

    if (failedCheck) {
      return (
        <Notification type={ `error` }>
          { failedCheck.progressMessage }
        </Notification>
      );
    }

    if (lastRun) {
      return (
        <>
          <Paragraph>
            { `Checked ${lastRun.checkedCount} mobile ${lastRun.checkedCount === 1 ? `number` : `numbers`}.` }
          </Paragraph>
          <Paragraph className={ `mt-2 font-semibold` }>
            {
              lastRun.invalidContactCount === 0 ?
                `No contacts have an invalid mobile number.` :
                `${lastRun.invalidContactCount} ${lastRun.invalidContactCount === 1 ? `contact has` : `contacts have`} an invalid mobile number.`
            }
          </Paragraph>
        </>
      );
    }

    return <Paragraph>{ `Mobile numbers haven't been checked in the last hour.` }</Paragraph>;
  }

  return (
    <Modal
      open={ open }
      setOpen={ hide }
      title={ runningCheck ? `Checking mobile numbers` : `Mobile number check` }
    >
      { renderBody() }

      <Paragraph
        variant={ `secondary` }
        className={ `mt-3` }
      >
        { `Only contacts with a value in their mobile number are checked. Contacts without one are skipped.` }
      </Paragraph>

      <div className={ `flex justify-end space-x-2 mt-4` }>
        <Button
          onClick={ hide }
          color={ `white` }
        >
          { `Close` }
        </Button>
        { !runningCheck && failedCheck && runButton(`Try again`) }
        { !runningCheck && !failedCheck && !lastRun && runButton(`Run check`) }
        { !runningCheck && !failedCheck && !!lastRun?.invalidContactCount && (
          <>
            { aiFixEnabled && (
              <Button
                onClick={ () => {
                  hide();
                  showFix();
                } }
                color={ `white` }
              >
                { `Fix with AI` }
              </Button>
            ) }
            <Button onClick={ onViewInvalid }>
              { `View invalid contacts` }
            </Button>
          </>
        ) }
      </div>
    </Modal>
  );
}
